import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { campaignAPI, socialAPI, referralAPI, brmAPI } from "@/services/api";
import { resolveReferralUrl } from "@/lib/referralUrl";
import { motion } from "framer-motion";
import {
  ArrowLeft, Copy, CheckCircle2, Users, Link2,
  TrendingUp, Clock, Zap, Share2, ExternalLink
} from "lucide-react";
import {
  TailAdminDetailRow,
  TailAdminEmptyState,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";
import CampaignTypeBadge from "@/components/campaign/CampaignTypeBadge";
import AudienceNegotiationPanel from "@/components/campaign/AudienceNegotiationPanel";

const GOAL_LABELS = {
  subscription: "Subscription", registration: "Registration",
  download: "Download", purchase: "Purchase",
};
const GOAL_COLORS = {
  subscription: "bg-violet-100 text-violet-700",
  registration: "bg-brand-50 text-brand-500",
  download: "bg-teal-100 text-teal-700",
  purchase: "bg-emerald-100 text-emerald-700",
};
const openValidatedHttps = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:") window.open(parsed.href, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Campaign destination is unavailable");
  }
};

const BRMCampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [myNiches, setMyNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);

  // Campaign deep-link (per BRM per campaign)
  const [referralLink, setReferralLink] = useState(null);

  // Attribution funnel (clicks → pending → converted)
  const [linkStats, setLinkStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [campaignHub, setCampaignHub] = useState(null);
  const [negotiations, setNegotiations] = useState([]);
  const [audienceSaving, setAudienceSaving] = useState(false);

  const fetchReferralLink = useCallback(async () => {
    try {
      const res = await referralAPI.getCampaignLink(campaignId);
      setReferralLink(res.data);
      // Also load attribution funnel stats
      setStatsLoading(true);
      const statsRes = await referralAPI.getCampaignStats(campaignId);
      setLinkStats(statsRes.data);
    } catch {
      // Link fetch failed - non-fatal
    } finally {
      setStatsLoading(false);
    }
  }, [campaignId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignRes, nichesRes] = await Promise.all([
        campaignAPI.getCampaign(campaignId),
        socialAPI.getMyNiches().catch(() => ({ niches: [] })),
      ]);
      const c = campaignRes.campaign;
      setCampaign(c);
      setMyNiches(nichesRes.niches || []);

      if (c.has_joined && c.goal_actionable !== false) {
        if (c.participation_status === "active") {
          fetchReferralLink();
        }
        try {
          const hubRes = await brmAPI.getCampaignHub(campaignId);
          setCampaignHub(hubRes.data);
        } catch {
          setCampaignHub(null);
        }
      }
    } catch {
      toast.error("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  }, [campaignId, fetchReferralLink]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (campaign?.has_joined && campaign?.request_type === "broadcast" && campaign?.audience_pay_enabled) {
      campaignAPI.listNegotiations(campaignId)
        .then((response) => setNegotiations(response.negotiations || []))
        .catch(() => setNegotiations([]));
    }
  }, [campaign, campaignId]);

  const handleAudienceSnapshot = async (data) => {
    setAudienceSaving(true);
    try {
      await campaignAPI.createAudienceSnapshot(campaignId, data);
      toast.success("Audience snapshot saved as unverified evidence");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save audience snapshot");
    } finally {
      setAudienceSaving(false);
    }
  };

  const handleNegotiation = async (data) => {
    setAudienceSaving(true);
    try {
      await campaignAPI.proposeNegotiation(campaignId, data);
      const response = await campaignAPI.listNegotiations(campaignId);
      setNegotiations(response.negotiations || []);
      toast.success("Private Negotiation Card sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send offer");
    } finally {
      setAudienceSaving(false);
    }
  };

  const handleNegotiationAction = async (cardVersionId, action) => {
    setAudienceSaving(true);
    try {
      if (action === "accept") await campaignAPI.acceptNegotiationVersion(campaignId, cardVersionId);
      if (action === "reject") await campaignAPI.rejectNegotiationVersion(campaignId, cardVersionId);
      if (action === "withdraw") await campaignAPI.withdrawNegotiationVersion(campaignId, cardVersionId);
      const response = await campaignAPI.listNegotiations(campaignId);
      setNegotiations(response.negotiations || []);
      await loadData();
      toast.success(action === "accept" ? "Your acceptance was recorded for this exact version" : `Offer ${action}ed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept offer");
    } finally {
      setAudienceSaving(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      const response = await campaignAPI.joinCampaign(campaignId);
      const participation = response?.data || response;
      toast.success(
        participation?.status === "pending_negotiation"
          ? "Application saved. Promotion unlocks after the terms are accepted."
          : "Joined! Your unique deep-link is ready."
      );
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join campaign");
    } finally {
      setJoining(false);
    }
  };

  const copyLink = () => {
    const url = resolveReferralUrl(referralLink?.referral_url, referralLink?.token);
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Deep-link copied! Share it to earn.");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = async () => {
    const url = resolveReferralUrl(referralLink?.referral_url, referralLink?.token);
    if (!url) return;
    if (navigator.share) {
      await navigator.share({
        title: campaign?.title,
        text: `Check this out - ${campaign?.title}`,
        url,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="px-4 py-6 text-center">
        <TailAdminEmptyState
          icon={Link2}
          title="Campaign not found"
          description="This campaign could not be loaded or is no longer available."
          action={<Button variant="outline" onClick={() => navigate("/brm/campaigns")}>Back to marketplace</Button>}
        />
      </div>
    );
  }

  const canJoin = campaign.goal_actionable !== false && campaign.next_action?.enabled !== false;
  const joinLabel = campaign.next_action?.label || "Join campaign";

  const matchedNiches = campaign.niches?.filter(n => myNiches.includes(n)) || [];
  const shareableUrl = resolveReferralUrl(referralLink?.referral_url, referralLink?.token);

  return (
    <div className="px-4 py-6 space-y-5 pb-24">

      {/* Back */}
      <button
        onClick={() => navigate("/brm/campaigns")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Campaigns
      </button>

      {/* Title */}
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <CampaignTypeBadge requestType={campaign.request_type} directVersion={campaign.direct_version} />
          {campaign.has_joined && (
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="w-3 h-3 mr-1" />Joined
            </span>
          )}
          {matchedNiches.length > 0 && !campaign.has_joined && (
            <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-500">
              <Zap className="w-3 h-3 mr-1" />Matches your niches
            </span>
          )}
        </div>
        <h1 className="text-2xl font-black tracking-tight">{campaign.title}</h1>
        {campaign.description && (
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            {campaign.description}
          </p>
        )}
      </div>

      {/* Commission card */}
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-theme-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-600 uppercase mb-1">Your commission</p>
              <p className="text-4xl font-black text-emerald-700">
                ₦{campaign.commission_per_goal?.toLocaleString()}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                per {GOAL_LABELS[campaign.goal_type]?.toLowerCase() || campaign.goal_type}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${GOAL_COLORS[campaign.goal_type] || "bg-gray-100"}`}>
              {GOAL_LABELS[campaign.goal_type] || campaign.goal_type}
            </div>
          </div>
      </section>

      <TailAdminPanel title="How you earn" action={null}>
        <p className="text-sm leading-6 text-gray-600">
          {campaign.request_type === "direct"
            ? `Acquire one customer deliberately, submit the ${campaign.direct_version === "v2" ? "founder's registration form" : "customer identifier"}, and earn after the qualifying event is verified.`
            : campaign.request_type === "broadcast"
              ? "Distribute your attributable link to your audience. Commission is earned from verified conversions; negotiated audience fees are governed by your private Negotiation Card."
              : "Follow the campaign instructions and earn after a verified conversion."}
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Performance commission</p>
            <p className="mt-1 font-bold text-emerald-800">₦{campaign.commission_per_goal?.toLocaleString()} per verified conversion</p>
          </div>
          {campaign.audience_pay_enabled && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Conditional audience/media earning</p>
              <p className="mt-1 text-sm font-medium text-amber-900">Private amount, trigger and deliverables are agreed in the Negotiation Card.</p>
            </div>
          )}
        </div>
      </TailAdminPanel>

      {campaign.has_joined && campaign.goal_actionable !== false && campaign.request_type === "broadcast" && campaign.audience_pay_enabled && (
        <TailAdminPanel
          title="Audience evidence & negotiation"
          description="Share capability evidence and agree private, versioned terms with the founder"
          action={null}
        >
          <AudienceNegotiationPanel
            defaultCommission={campaign.commission_per_goal}
            audiencePayModel={campaign.audience_pay_model}
            onSnapshot={handleAudienceSnapshot}
            onPropose={handleNegotiation}
            loading={audienceSaving}
          />
          {negotiations.length > 0 && (
            <div className="mt-5 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Negotiation history</p>
              {negotiations.map((card) => (
                <div key={card.card_version_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 p-3 text-sm">
                  <div>
                    <p className="font-semibold">Version {card.version} · {card.status}</p>
                    <p className="text-xs text-gray-500">₦{card.commission_per_goal.toLocaleString()} / conversion · ₦{card.media_fee.toLocaleString()} media fee</p>
                  </div>
                  {card === negotiations[0] && ["offered", "proposed"].includes(card.status) && (
                    <div className="flex gap-2">
                      {card.created_by_type === "client" ? (
                        <>
                          <Button size="sm" onClick={() => handleNegotiationAction(card.card_version_id, "accept")} disabled={audienceSaving}>
                            Accept exact version
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleNegotiationAction(card.card_version_id, "reject")} disabled={audienceSaving}>
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleNegotiationAction(card.card_version_id, "withdraw")} disabled={audienceSaving}>
                          Withdraw my offer
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TailAdminPanel>
      )}

      {campaign.has_joined && campaignHub?.enrollments_summary && (
        <TailAdminPanel
          title="Your campaign activity"
          description="Enrollments and earnings for this Prove-Request"
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/brm/campaigns/${campaignId}/hub`)}
            >
              Open campaign hub
            </Button>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Enrollments</p>
              <p className="text-xl font-bold">{campaignHub.enrollments_summary.total}</p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-xl font-bold">{campaignHub.enrollments_summary.pending}</p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Verified</p>
              <p className="text-xl font-bold">{campaignHub.enrollments_summary.verified}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3">
              <p className="text-xs text-emerald-600">Earned here</p>
              <p className="text-xl font-bold text-emerald-700">
                ₦{(campaignHub.earnings_on_campaign || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </TailAdminPanel>
      )}

      {/* ── CAMPAIGN DEEP-LINK SECTION (joined BRMs only) ─────────────────────── */}
      {campaign.has_joined && campaign.participation_status === "active" && campaign.goal_actionable !== false && campaign.request_type === "broadcast" && (
        <div className="space-y-3">
          {/* Header explaining what this link is */}
          <div>
            <p className="text-xs font-bold text-brand-500 uppercase tracking-widest">Your Campaign Deep-Link</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Unique to you + this campaign. Every click and conversion tracks back to you for 30 days.
            </p>
          </div>

          {referralLink ? (
            <>
              {/* Link display */}
              <section className="rounded-2xl border border-brand-200 bg-brand-50 p-4 shadow-theme-xs">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 bg-white rounded-lg p-3">
                    <Link2 className="w-4 h-4 text-brand-500 shrink-0" />
                    <p className="flex-1 text-xs font-mono text-gray-500 break-all">
                      {shareableUrl}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      className="h-10 rounded-xl"
                      onClick={copyLink}
                    >
                      {copied
                        ? <><CheckCircle2 className="w-4 h-4 mr-2" />Copied!</>
                        : <><Copy className="w-4 h-4 mr-2" />Copy Link</>
                      }
                    </Button>
                    <Button
                      variant="outline"
                      className="h-10 rounded-xl"
                      onClick={shareLink}
                    >
                      <Share2 className="w-4 h-4 mr-2" />Share
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">
                    Share on Instagram, TikTok, WhatsApp, or anywhere your audience is.
                  </p>
                </div>
              </section>

              {/* Attribution funnel - "the Alice view" */}
              {statsLoading ? (
                <Skeleton className="h-28 w-full rounded-2xl" />
              ) : linkStats ? (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-theme-xs">
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-amber-700 uppercase">Your Attribution Funnel</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: "Clicks", value: linkStats.clicks ?? referralLink.clicks, icon: TrendingUp, color: "text-brand-500", bg: "bg-brand-50" },
                        { label: "In Window", value: linkStats.pending_in_window ?? 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-100" },
                        { label: "Converted", value: linkStats.conversions ?? referralLink.conversions, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
                      ].map(({ label, value, icon: Icon, color, bg }) => (
                        <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
                          <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
                          <p className={`text-xl font-black ${color}`}>{value}</p>
                          <p className="text-[10px] font-medium text-gray-500">{label}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-amber-700 leading-relaxed">
                      <strong>In Window</strong> = people who clicked your link and are still within
                      the 30-day attribution period. They haven't converted yet but still can -
                      and you'll get the commission when they do.
                    </p>
                    {linkStats.conversion_rate > 0 && (
                      <p className="text-[10px] text-gray-500 text-center">
                        Your conversion rate: <strong className="text-foreground">{linkStats.conversion_rate?.toFixed(1)}%</strong>
                      </p>
                    )}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
            <Skeleton className="h-24 w-full rounded-2xl" />
          )}
        </div>
      )}

      {/* Campaign details */}
      <TailAdminPanel title="Campaign Details" action={null}>
          <div className="space-y-3">
            {[
              { label: "Participants", value: `${campaign.participation_count} BRMs promoting` },
              campaign.budget_cap && {
                label: "Budget",
                value: `₦${campaign.budget_spent?.toLocaleString()} / ₦${campaign.budget_cap?.toLocaleString()} used`
              },
              campaign.min_followers > 0 && {
                label: "Min. followers",
                value: campaign.min_followers.toLocaleString()
              },
              campaign.ends_at && {
                label: "Ends",
                value: new Date(campaign.ends_at).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })
              },
            ].filter(Boolean).map(({ label, value }) => (
              <TailAdminDetailRow key={label} label={label} value={value} />
            ))}
          </div>
      </TailAdminPanel>

      {/* Niches */}
      {campaign.niches?.length > 0 && (
        <TailAdminPanel title="Target Niches" action={null}>
            <div className="flex flex-wrap gap-2">
              {campaign.niches.map(n => (
                <span
                  key={n}
                  className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                    myNiches.includes(n)
                      ? "border-brand-300 bg-brand-50 text-brand-500"
                      : "border-gray-200 text-gray-500"
                  }`}
                >
                  {n}
                </span>
              ))}
            </div>
            {matchedNiches.length > 0 && (
              <p className="text-xs text-brand-500 mt-2 font-medium">
                {matchedNiches.length} niche{matchedNiches.length > 1 ? "s" : ""} match your profile
              </p>
            )}
        </TailAdminPanel>
      )}

      {/* Target URL */}
      {campaign.target_url && (
        <button
          className="w-full flex items-center gap-2 text-xs text-gray-500 hover:text-brand-500 transition-colors"
          onClick={() => openValidatedHttps(campaign.target_url)}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          <span className="truncate">{campaign.target_url}</span>
        </button>
      )}

      {/* Join CTA - not joined */}
      {!campaign.has_joined && (
        <div className="fixed bottom-20 left-4 right-4 z-10">
          <Button
            className="w-full h-14 rounded-2xl shadow-2xl shadow-theme-xs text-base font-bold"
            onClick={handleJoin}
            disabled={joining || !canJoin}
          >
            {joining ? "Joining..." : <>{joinLabel} {canJoin && <Link2 className="w-5 h-5 ml-2" />}</>}
          </Button>
          <p className="text-[10px] text-gray-500 text-center mt-2">
            {canJoin
              ? "You'll receive a unique deep-link immediately after joining."
              : "Proovam V1 supports subscription goals. This goal will become actionable in a later release."}
          </p>
        </div>
      )}

      {/* Enrolled state CTA — hide join, show enroll + hub */}
      {campaign.has_joined && (
        <div className="fixed bottom-20 left-4 right-4 z-10 space-y-2">
          {campaign.request_type !== "broadcast" && campaign.goal_actionable !== false && (
            <Button
              className="w-full h-14 rounded-2xl shadow-2xl shadow-theme-xs text-base font-bold"
              onClick={() =>
                navigate(`/brm/enroll?campaign_id=${campaignId}&product_id=${campaign.product_id}`)
              }
              data-testid="enroll-user-cta"
            >
              <Users className="w-5 h-5 mr-2" /> Enroll User on This Campaign
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full h-11 rounded-2xl bg-white"
            onClick={() =>
              navigate(
                `/brm/messages?campaignId=${campaignId}&clientId=${campaign.client_id}`
              )
            }
          >
            Message client about this Prove-Request
          </Button>
          {campaign.participation_status && (
            <p className="text-[10px] text-emerald-600 text-center font-medium">
              Participation: {campaign.participation_status}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BRMCampaignDetail;
